import { _ } from "extensions/underscore";
import { startsWith, debounce } from "lodash-es";

import { destroyPopovers, updateQueryStringParameters } from "common/utils";

import { store } from "common/store";
import { emitter } from "common/emitter";
import { watch } from "common/components/watch";

// eslint-disable-next-line import/no-cycle
import {
    SERIALIZE_COMPARE_DATES_URL,
    SERIALIZE_DATES_FROM_STORE,
    CLEAR_HISTORY_STACK,
    SET_ACTIVE_ROUTE
} from "common/actions/common";

import { removeQueryStringParameter } from "common/helpers/removeQueryStringParameter";

import { pageTracker } from "common/components/pageTracker";

import { fragmentToProduct } from "common/routers/products";

import { history } from "./history";

class PazienHistory {
    constructor() {
        this.subscribeToStore();
        
        emitter.on("logout:complete", () => {
            this.unsubscribe.forEach((u) => u());
            CLEAR_HISTORY_STACK();
        });
    }

    unsubscribe = [];

    static getMerchantAliasOrRef() {
        const state = store.getState();
        if (state.common.merchant_config.alias) {
            return state.common.merchant_config.alias;
        }

        return state.common.merchant_ref;
    }

    isAdminApp(fragment = this.getCurrentFragment()) {
        return startsWith(fragment, "administrator/");
    }

    subscribeToStore() {
        const subscriptions = [
            {
                objectPath: "common.compare_to",
                compare: (prev, next) => prev.length === next.length && next.every(({ startDate, endDate }, i) => prev[i] && prev[i].startDate === startDate && prev[i].endDate === endDate),
                watcherCb: this.setCompare.bind(this)
            },
            {
                objectPath: "common.date_range.startDate",
                compare: (prev, next) => prev === next,
                watcherCb: this.setDates.bind(this)
            },
            {
                objectPath: "common.date_range.endDate",
                compare: (prev, next) => prev === next,
                watcherCb: this.setDates.bind(this)
            },
            {
                objectPath: "common.date_range.datetype",
                compare: (prev, next) => prev === next,
                watcherCb: this.setDatetype.bind(this)
            },
            {
                objectPath: "common.currency",
                compare: (prev, next) => prev === next,
                watcherCb: this.setCurrency.bind(this)
            },
            {
                objectPath: "common.bu_ref",
                compare: (prev, next) => prev === next,
                watcherCb: this.setBuRef.bind(this)
            },
            {
                objectPath: "common.merchant_ref",
                compare: (prev, next) => prev === next,
                watcherCb: this.setMerchantRef.bind(this)
            },
            {
                objectPath: "common.merchant_config.alias", // Also update the queryparams when received an alias for the merchant
                compare: (prev, next) => prev === next,
                watcherCb: this.setMerchantRef.bind(this)
            }
        ];

        this.unsubscribe = subscriptions.map(({ objectPath, compare, watcherCb }) => {
            const w = watch(store.getState, objectPath, compare);
            return store.subscribe(w(watcherCb));
        });
    }

    setCurrency() {
        const currency = store.getState().common.currency;
        const merchantDefaultCurrency = store.getState().common.merchant_config?.default_currency;

        if (currency === merchantDefaultCurrency) {
            this.unsetQueryStringParameters({
                param: "currency",
                trigger: false
            });
        } else {
            this.setQueryStringParameters({
                param: "currency",
                value: currency,
                trigger: false
            });
        }
    }

    setMerchantRef() {
        this.setQueryStringParameters({
            param: "merchant",
            value: PazienHistory.getMerchantAliasOrRef(),
            trigger: false
        });
    }

    setBuRef() {
        const bu_ref = store.getState().common.bu_ref;

        if (bu_ref) {
            this.setQueryStringParameters({
                param: "bu",
                value: bu_ref,
                trigger: false
            });
        } else {
            this.unsetQueryStringParameters({
                param: "bu",
                trigger: false
            });
        }
    }

    setDates() {
        const { dates } = SERIALIZE_DATES_FROM_STORE();

        if (dates) {
            this.setQueryStringParameters({
                param: "dates",
                value: dates,
                trigger: false
            });
        } else {
            this.unsetQueryStringParameters({
                param: "dates",
                trigger: false
            });
        }
    }

    setDatetype() {
        const { datetype } = SERIALIZE_DATES_FROM_STORE();

        if (datetype) {
            this.setQueryStringParameters({
                param: "datetype",
                value: datetype,
                trigger: false
            });
        } else {
            this.unsetQueryStringParameters({
                param: "datetype",
                trigger: false
            });
        }
    }

    setCompare(newVal = []) {
        if (newVal.length) {
            this.setQueryStringParameters({
                param: "compare",
                value: SERIALIZE_COMPARE_DATES_URL(),
                trigger: false
            });
        } else {
            this.unsetQueryStringParameters({
                param: "compare",
                trigger: false
            });
        }
    }

    static getStoreParams() {
        const { dates, datetype } = SERIALIZE_DATES_FROM_STORE();

        const params = {
            compare: SERIALIZE_COMPARE_DATES_URL(),
            merchant: PazienHistory.getMerchantAliasOrRef(),
            bu: store.getState().common.bu_ref,
            currency: store.getState().common.currency_source === "default" ? "" : store.getState().common.currency,
            dates,
            datetype
        };

        // Remove currency if it is the same as merchant default
        if (params.currency === store.getState().common.merchant_config?.default_currency) {
            delete params.currency;
        }

        return params;
    }

    // eslint-disable-next-line class-methods-use-this
    getCurrentFragmentProduct() {
        return fragmentToProduct(window.location.pathname);
    }

    // The fragment is considered  to be everything in the URL past `/merchant/`
    // eslint-disable-next-line class-methods-use-this
    getCurrentFragment() {
        return window.location.pathname.replace("/merchant/", "").concat(window.location.search);
    }

    // eslint-disable-next-line class-methods-use-this
    addParamsToFragment(fragment, extraParams = {}) {
        const params = {
            ...PazienHistory.getStoreParams(),
            ...extraParams
        };

        const filteredParams = Object.entries(params).reduce((memo, [key, value]) => {
            if (value === "" || typeof value === "undefined") {
                return memo;
            }
            return [...memo, [key, value]];
        }, []);

        let newFragment = removeQueryStringParameter(fragment); // Remove ALL existing params from the url
        if (filteredParams.length) {
            const queryString = filteredParams.reduce((components, [key, value]) => {
                if (value === null) {
                    return components;
                }
                return [...components, `${key}=${value}`];
            }, []).join("&");

            newFragment = `${newFragment}?${queryString}`;
        }

        return newFragment;
    }

    navigateWithParams(fragment, opts) {
        const options = {
            trigger: false,
            replace: false,
            extraParams: {},
            ...opts
        };

        const newFragment = this.addParamsToFragment(fragment, options.extraParams);

        // Check if newFragment is the same as the last known and we don't trigger then return earlier
        if (this.getCurrentFragment() === newFragment && options.trigger === false) {
            return this;
        }

        this.navigate(newFragment, options);

        return this;
    }

    static debouncedNavigationReporter = debounce(({ from, to }) => {
        emitter.emit("tracking.track", {
            type: "navigate",
            data: {
                from,
                to
            }
        });
    }, 500);

    navigate(fragment, options = {}) {
        pageTracker.makeNewRequestGroupID();

        // Clean up pesky tooltips and popovers when navigating to another page
        destroyPopovers();

        if (this.getCurrentFragment() !== fragment) {
            PazienHistory.debouncedNavigationReporter({
                from: this.getCurrentFragment(),
                to: fragment
            });
        }

        // If routes are not prepended with `/` the fragment is appended to the existing one, instead of replacing.
        // Example
        // window.location.pathname:  "/merchant/analytics/authorizations"
        // window.history.replaceState({}, "", "analytics/fees")  // no preceding `/`
        // window.location.pathname:  "/merchant/analytics/analytics/fees"
        if (!fragment.startsWith("/merchant/")) {
            if (fragment.startsWith("/")) {
                fragment = fragment.slice(1);
            }
            fragment = `/merchant/${fragment}`;
        }

        if (options.trigger) {
            SET_ACTIVE_ROUTE(fragment);
            if (options.replace) {
                return history.replace(fragment);
            }
            return history.push(fragment);
        }

        // React Router listens to history object's location. If no trigger is desired then the history method(s) cannot be called.
        // This will _only_ update the URL bar. No components will see the updates from React Router.
        if (options.replace) {
            return window.history.replaceState({}, "", fragment);
        }
        return window.history.pushState({}, "", fragment);
    }

    unsetQueryStringParameters(opts) {
        const options = {
            param: void 0,
            trigger: false,
            ...opts
        };

        // eslint-disable-next-line eqeqeq
        if (options.param == null) {
            return;
        }

        const currentFragment = this.getCurrentFragment();
        const newFragment = removeQueryStringParameter(currentFragment, options.param);

        // There is no need to execute `navigate` when we don't trigger and fragmets are the same
        if (currentFragment === newFragment && options.trigger === false) {
            return;
        }

        this.navigate(newFragment, {
            replace: true,
            trigger: !!options.trigger
        });
    }

    setQueryStringParameters(opts = {}) {
        const options = {
            param: void 0,
            value: void 0,
            trigger: false,
            ...opts
        };

        // eslint-disable-next-line eqeqeq
        if (options.param == null || options.value == null) {
            return;
        }

        const currentFragment = this.getCurrentFragment();
        const newFragment = updateQueryStringParameters(currentFragment, options.param, options.value);

        this.navigate(newFragment, {
            replace: true,
            trigger: !!options.trigger
        });
    }

    // eslint-disable-next-line class-methods-use-this
    parseQueryString(queryString = window.location.search) {
        if (!queryString) {
            return {};
        }

        return queryString.replace("?", "").split("&").reduce((memo, param) => {
            const [key, value] = param.split("=");
            return {
                ...memo,
                [key]: _.escape(value)
            };
        }, {});
    }
}

export const pazienHistory = new PazienHistory();
